import { Controller, type Control } from 'react-hook-form';
import { formatDateSafe } from '@/utils/dateUtils';
import type { VacationRequestFormValues } from '../../../models/vacation.schemas';
import type { ValidationIssue } from '../../../utils/vacationRules';
import { PERIOD_FIELD_LABELS } from '../vacationRequestForm.definitions';
import IsoDatePicker from './IsoDatePicker';

export interface VacationPeriodFieldsProps {
    isFractioned: boolean;
    isSubmitting: boolean;
    control: Control<VacationRequestFormValues>;
    getFieldError: (name: keyof VacationRequestFormValues) => string | undefined;
    firstEndOptions: string[];
    canSelectFirstEnd: boolean;
    canSelectSecondStart: boolean;
    firstStartDefaultMonth?: string;
    secondStartDefaultMonth?: string;
    secondEndDate: string;
    isStartDateSelectable: (iso: string) => boolean;
    isSecondStartSelectable: (iso: string) => boolean;
    onFirstStartChange: (iso: string) => void;
    onFirstEndChange: (iso: string) => void;
    onSecondStartChange: (iso: string) => void;
    fieldErrors?: ValidationIssue[];
    fieldWarnings?: ValidationIssue[];
}

function FieldError({ message }: { message?: string }) {
    if (!message) return null;
    return <p className="mt-1 text-xs text-red-600 font-montserrat">{message}</p>;
}

function FieldRuleIssues({
    fieldName,
    errors,
    warnings,
}: {
    fieldName: string;
    errors?: ValidationIssue[];
    warnings?: ValidationIssue[];
}) {
    const fieldErrs = errors?.filter((e) => e.field === fieldName) ?? [];
    const fieldWarns = warnings?.filter((e) => e.field === fieldName) ?? [];

    return (
        <>
            {fieldErrs.map((issue) => (
                <p key={issue.code} className="mt-1 text-xs text-red-600 font-montserrat">
                    {issue.message}
                </p>
            ))}
            {fieldWarns.map((issue) => (
                <p key={issue.code} className="mt-1 text-xs text-amber-700 font-montserrat">
                    {issue.message}
                </p>
            ))}
        </>
    );
}

export function VacationPeriodFields({
    isFractioned,
    isSubmitting,
    control,
    getFieldError,
    firstEndOptions,
    canSelectFirstEnd,
    canSelectSecondStart,
    firstStartDefaultMonth,
    secondStartDefaultMonth,
    secondEndDate,
    isStartDateSelectable,
    isSecondStartSelectable,
    onFirstStartChange,
    onFirstEndChange,
    onSecondStartChange,
    fieldErrors,
    fieldWarnings,
}: VacationPeriodFieldsProps) {
    if (!isFractioned) {
        return (
            <div>
                <label
                    htmlFor="firstStartDate"
                    className="block text-sm font-medium text-raro-gray-700 font-montserrat"
                >
                    {PERIOD_FIELD_LABELS.singleStart}
                </label>
                <div className="mt-1">
                    <Controller
                        name="firstStartDate"
                        control={control}
                        render={({ field }) => (
                            <IsoDatePicker
                                id="firstStartDate"
                                value={field.value}
                                onChange={onFirstStartChange}
                                onBlur={field.onBlur}
                                disabled={isSubmitting}
                                isDateSelectable={isStartDateSelectable}
                                defaultMonthIso={firstStartDefaultMonth}
                            />
                        )}
                    />
                </div>
                <FieldError message={getFieldError('firstStartDate')} />
                <FieldRuleIssues
                    fieldName="firstStartDate"
                    errors={fieldErrors}
                    warnings={fieldWarnings}
                />
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
                <label
                    htmlFor="firstStartDate"
                    className="block text-sm font-medium text-raro-gray-700 font-montserrat"
                >
                    {PERIOD_FIELD_LABELS.firstStart}
                </label>
                <div className="mt-1">
                    <Controller
                        name="firstStartDate"
                        control={control}
                        render={({ field }) => (
                            <IsoDatePicker
                                id="firstStartDate"
                                value={field.value}
                                onChange={onFirstStartChange}
                                onBlur={field.onBlur}
                                disabled={isSubmitting}
                                isDateSelectable={isStartDateSelectable}
                                defaultMonthIso={firstStartDefaultMonth}
                            />
                        )}
                    />
                </div>
                <FieldError message={getFieldError('firstStartDate')} />
                <FieldRuleIssues
                    fieldName="firstStartDate"
                    errors={fieldErrors}
                    warnings={fieldWarnings}
                />
            </div>

            <div>
                <label
                    htmlFor="firstEndDate"
                    className="block text-sm font-medium text-raro-gray-700 font-montserrat"
                >
                    {PERIOD_FIELD_LABELS.firstEnd}
                </label>
                <div className="mt-1">
                    <Controller
                        name="firstEndDate"
                        control={control}
                        render={({ field }) => (
                            <IsoDatePicker
                                id="firstEndDate"
                                value={field.value}
                                onChange={onFirstEndChange}
                                onBlur={field.onBlur}
                                disabled={isSubmitting || !canSelectFirstEnd}
                                allowedDates={firstEndOptions}
                            />
                        )}
                    />
                </div>
                <FieldError message={getFieldError('firstEndDate')} />
                <FieldRuleIssues
                    fieldName="firstEndDate"
                    errors={fieldErrors}
                    warnings={fieldWarnings}
                />
            </div>

            <div>
                <label
                    htmlFor="secondStartDate"
                    className="block text-sm font-medium text-raro-gray-700 font-montserrat"
                >
                    {PERIOD_FIELD_LABELS.secondStart}
                </label>
                <div className="mt-1">
                    <Controller
                        name="secondStartDate"
                        control={control}
                        render={({ field }) => (
                            <IsoDatePicker
                                id="secondStartDate"
                                value={field.value}
                                onChange={onSecondStartChange}
                                onBlur={field.onBlur}
                                disabled={isSubmitting || !canSelectSecondStart}
                                isDateSelectable={isSecondStartSelectable}
                                defaultMonthIso={secondStartDefaultMonth}
                            />
                        )}
                    />
                </div>
                <FieldError message={getFieldError('secondStartDate')} />
                <FieldRuleIssues
                    fieldName="secondStartDate"
                    errors={fieldErrors}
                    warnings={fieldWarnings}
                />
            </div>

            <div>
                <label
                    htmlFor="secondEndDate"
                    className="block text-sm font-medium text-raro-gray-700 font-montserrat"
                >
                    {PERIOD_FIELD_LABELS.secondEnd}
                </label>
                <input
                    id="secondEndDate"
                    type="text"
                    value={secondEndDate ? formatDateSafe(secondEndDate) : ''}
                    readOnly
                    disabled
                    placeholder="—"
                    className="mt-1 block w-full rounded-xl border border-primary-200 bg-gray-50 p-2.5 text-sm font-montserrat text-raro-gray-500"
                />
            </div>
        </div>
    );
}

export default VacationPeriodFields;
